import _ from 'lodash';
import moment from 'moment-timezone';
import { sqDurationStore, sqTrendCapsuleStore, sqTrendStore } from '@/core/core.stores';
import { NUMBER_CONVERSIONS } from '@/main/app.constants';
import { getDefaultCompareViewPropertyValue } from '@/services/compareView.utilities';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { getSeriesIdsByCalculationType } from '@/trend/trendDataHelper.utilities';
import { COMMON_PROPS, type PROPS_TO_DEHYDRATE } from '@/trendData/baseItem.constants';
import { BaseItemStore } from '@/trendData/baseItem.store';
import { createItem, pruneDehydratedItemProps, shouldDehydrateItem } from '@/trendData/itemStore.utilities';
import { removeColor } from '@/trendData/itemStoreColor.utilities';
import { createStringEnum, getDataProps, transformDataPoints } from '@/trendData/seriesData.utilities';
import { getChartWidth } from '@/trendData/trend.actions';
import {
  DASH_STYLES,
  ITEM_CHILDREN_TYPES,
  ITEM_TYPES,
  PREVIEW_ID,
  PREVIEW_PREFIX,
  SAMPLE_OPTIONS,
  Y_AXIS_TYPES,
} from '@/trendData/trendData.constants';
import { createUniqueCapsuleSeriesId, getMinTime } from '@/trendData/trendSeries.utilities';
import { getYAxisConfig, updateComputedYAxisExtremes } from '@/trendData/yAxis.actions';
import { getCapsuleDuration } from '@/utilities/utilities';
import type { SeriesDataContext } from '@/trendData/seriesData.utilities';
import type { Asset, CapsuleItem, CapsuleToAdd, DataSample, SeriesItem, StringEnum } from '@/trendData/trendData.types';
import type { ParametersMap } from '@/utilities/formula.constants';

type SeriesInterpolation = string | { value?: string | null } | null | undefined;

export type SeriesDataSample = Partial<DataSample> & { key: number };

interface AddSeriesPayload {
  id: string;
  name: string;
  lane: number;
  alignment: string;
  color?: string;
  interpolationMethod?: SeriesInterpolation;
  isArchived?: boolean;
  effectivePermissions?: unknown;
  laneReservation?: string;
}

type TrendCapsuleStoreItem = CapsuleToAdd & { selected?: boolean; duration?: number; id: string };

type SeriesDataPoint =
  | [number, number | null | undefined]
  | [number, number | null | undefined, number | null | undefined]
  | { x: number; y: number | null | undefined; marker?: { enabled: boolean; symbol: string; radius: number } };

export type SeriesStoreItem = Omit<
  SeriesItem,
  | 'zones'
  | 'capsuleSegmentData'
  | 'samples'
  | 'data'
  | 'stringEnum'
  | 'assets'
  | 'dataStatus'
  | 'datasource'
  | 'description'
  | 'displayUnitOfMeasure'
> & {
  id: string;
  interestId?: string;
  capsuleId?: string;
  duration: number | undefined;
  startTime?: number | null;
  endTime?: number | null;
  otherChildrenOf?: Array<string | undefined>;
  childType?: string;
  idChildOf?: string;
  rightAxis?: boolean;
  showDataLabels?: boolean;
  column?: string;
  colorBy?: string;
  stringEnum?: StringEnum[];
  capsuleSegmentSamples?: SeriesDataSample[];
  capsuleSegmentData?: SeriesDataPoint[];
  data: SeriesDataPoint[];
  zones?: Array<{ color?: string }>;
  shadedAreaLower?: number | null;
  shadedAreaUpper?: number | null;
  samples: SeriesDataSample[];
  assets: Asset[];
  dataStatus?: string;
  datasource?: { name: string };
  description?: string;
  displayUnitOfMeasure?: unknown;
  dirty?: boolean;
} & Record<string, unknown>;

type SeriesProps = Partial<SeriesStoreItem> & Record<string, unknown>;

type SeriesStoreItemDehydrated = Partial<
  Pick<SeriesStoreItem, Extract<(typeof PROPS_TO_DEHYDRATE)[number], keyof SeriesStoreItem>>
> & {
  id: string;
  name: string;
  dashStyle: string;
  lane: number;
  alignment?: string;
  interpolationMethod?: SeriesInterpolation;
};

interface AddSeriesFromCapsulePayload {
  capsule: TrendCapsuleStoreItem & { selected?: boolean };
  interest: SeriesStoreItem;
}

export interface AddDataPayload {
  id: string;
  samples: SeriesDataSample[];
  valueUnitOfMeasure?: string;
  warningCount?: number;
  warningLogs?: unknown[] | unknown;
  timingInformation?: string;
  meterInformation?: string;
  interpolationMethod?: string;
  stringEnum?: StringEnum[];
  capsuleSegmentSamples?: SeriesDataSample[];
  color?: string;
}

interface SignalSegmentsPayload extends Omit<AddDataPayload, 'samples'> {
  id: string;
  samples: SeriesDataSample[];
  requestCapsules: Array<{
    interestId: string;
    capsuleId: string;
    startTime: number;
    endTime: number;
  }>;
}

interface PreviewSeriesDefinition {
  formula?: string;
  parameters?: ParametersMap;
  id?: string;
  color?: string | null;
  sampleDisplayOption?: string;
}

interface PreviewSeriesPayload {
  formula?: string;
  parameters?: ParametersMap;
  id: string;
  name: string;
  samples: SeriesDataSample[];
  valueUnitOfMeasure?: string;
  lane: number;
  alignment: string;
  color?: string | null;
  lineWidth?: number;
  sampleDisplayOption?: string;
  timingInformation?: string;
  meterInformation?: string;
  interpolationMethod?: string;
  stringEnum?: StringEnum[];
  capsuleSegmentSamples?: SeriesDataSample[];
}

type TrendSeriesStoreState = {
  items: SeriesStoreItem[];
  previewChartItem: SeriesStoreItem | SeriesStoreItem[] | undefined;
  editingId?: string;
  previewSeriesDefinition?: PreviewSeriesDefinition | null;
  primarySeries: any; // any because of Baobab's `monkey()`
  itemsAndPreview: any; // any because of Baobab's `monkey()`
};

export type TrendSeriesDehydratedState = {
  items: SeriesStoreItemDehydrated[];
  editingId?: string | null;
  previewSeriesDefinition?: PreviewSeriesDefinition | null;
};

/**
 * Chart geometry and visible range the sample transforms need. Reads the duration and trend stores,
 * which store code should not do — transitional only. E4 moves the derivation into the series
 * runtime store, whose typed action takes this as a parameter, and this goes away with it.
 */
function getSeriesDataContext(): SeriesDataContext {
  const { start, end, duration } = sqDurationStore.displayRange;
  return {
    chartWidth: getChartWidth(),
    displayRange: { start: start.valueOf(), end: end.valueOf(), duration: duration.asMilliseconds() },
  };
}

/**
 * A store for containing timeseries which can be displayed on the chart. A series is composed of an array of data
 * points and their associated timestamps.
 *
 * This store is augmented with additional functionality from BaseItemStore.
 */
export class TrendSeriesStore extends BaseItemStore<SeriesItem> {
  static readonly storeName = 'sqTrendSeriesStore';

  initialize(): void {
    const initialState: TrendSeriesStoreState = {
      ...COMMON_PROPS,
      // Avoid clearing state that is not dehydrated when the store is re-initialized. Async calls will repopulate
      // these properties as needed
      previewChartItem: this.state ? this.state.get('previewChartItem') : undefined,
      // all series that are not converted from capsules
      primarySeries: this.monkey(['items'], (items: SeriesStoreItem[] = []) => items.filter((item) => !item.isChildOf)),
      itemsAndPreview: this.monkey(
        ['items'],
        ['editingId'],
        ['previewSeriesDefinition'],
        ['previewChartItem'],
        (
          items: SeriesStoreItem[] | undefined,
          editingId: string | null | undefined,
          previewSeriesDefinition: PreviewSeriesDefinition | null | undefined,
          previewChartItem: SeriesStoreItem | SeriesStoreItem[] | undefined,
        ): SeriesStoreItem[] => {
          const series = [...(items ?? [])];
          const previewItems = Array.isArray(previewChartItem)
            ? previewChartItem.filter(Boolean)
            : previewChartItem
              ? [previewChartItem]
              : [];
          const displayPreview = !_.isEmpty(previewSeriesDefinition) && previewItems.length > 0;

          if (editingId != null && displayPreview) {
            const itemIndex = series.findIndex((item) => item.id === editingId);
            if (itemIndex >= 0 && previewItems[0]) {
              series.splice(itemIndex, 1, previewItems[0]);
            } else {
              series.push(...previewItems);
            }
          } else if (displayPreview) {
            series.push(...previewItems);
          }

          return series;
        },
      ),
    };
    this.state = this.immutable(initialState);
  }

  /** Generates capsule series.
   *
   * Use the `useCapsuleSeries` hook in components to minimize re-renders */
  get capsuleSeries(): SeriesStoreItem[] {
    return this.getCapsuleSeries();
  }

  get nonCapsuleSeries(): SeriesStoreItem[] {
    return this.getNonCapsuleSeries();
  }

  get primarySeries(): SeriesStoreItem[] {
    return this.state.get('primarySeries') ?? [];
  }

  /**
   * Gets the longest capsule series duration
   *
   * @return The longest capsule series duration or zero if there are no capsule series.
   */
  get longestCapsuleSeriesDuration(): number {
    // Be sure to include in progress capsules
    let longestInProgress = 0;
    const inProgressCapsuleSeries = this.getCapsuleSeries().filter((series) => series.duration == null);
    if (inProgressCapsuleSeries.length > 0) {
      const earliestStart = _.min(
        inProgressCapsuleSeries
          .map((series) => series.startTime)
          .filter((start): start is number => typeof start === 'number'),
      );
      if (typeof earliestStart === 'number') {
        longestInProgress = moment.duration(this.getAlternateEndTime() - earliestStart).asMilliseconds();
      }
    }
    const longestDuration = _.maxBy(this.getCapsuleSeries(), (series) => series.duration ?? 0)?.duration ?? 0;
    return Math.max(longestInProgress, longestDuration);
  }

  /**
   * Returns the Id of the series that is being edited.
   */
  get editingId(): string | undefined {
    return this.state.get('editingId');
  }

  /**
   * Returns an Object describing a series (so that it can be updated upon scrolling)
   */
  get previewSeriesDefinition(): PreviewSeriesDefinition | null | undefined {
    return this.state.get('previewSeriesDefinition');
  }

  /**
   * Returns the Store.nonCapusleSeries with the preview added
   */
  get nonCapsuleSeriesAndPreview(): SeriesStoreItem[] {
    return this.addPreview(this.getNonCapsuleSeries());
  }

  /**
   * Returns all the Store.items with the preview added
   */
  get itemsAndPreview(): SeriesStoreItem[] {
    return this.state.get('itemsAndPreview') ?? [];
  }

  /**
   * Returns the preview series
   */
  get previewChartItem(): SeriesStoreItem | SeriesStoreItem[] | undefined {
    return this.state.get('previewChartItem');
  }

  private get seriesItems(): SeriesStoreItem[] {
    return (this.state.get('items') as SeriesStoreItem[]) ?? [];
  }

  /**
   * Updates series from uncertain capsules based on the input map of old uncertain capsule ids to new replace
   * capsules. Id, start, end, and duration of capsules will be updated.
   *
   * @param {Object} map - A map of uncertain capsule ids to their replacement capsules
   */
  syncUncertainCapsuleSeries(map: Record<string, CapsuleToAdd>): void {
    Object.entries(map).forEach(([oldCapsuleId, newCapsule]) => {
      const matchingSeries = this.seriesItems.filter((item) => item.capsuleId === oldCapsuleId);
      matchingSeries.forEach((series) => {
        const index = this.findItemIndex(series.id);
        if (index > -1) {
          this.state.merge(['items', index], {
            id: createUniqueCapsuleSeriesId(newCapsule.id, series.interestId ?? ''),
            capsuleId: newCapsule.id,
            startTime: newCapsule.startTime,
            endTime: newCapsule.endTime,
            duration: getCapsuleDuration(newCapsule.startTime, newCapsule.endTime),
            otherChildrenOf: [newCapsule.isChildOf, newCapsule.id].filter(Boolean),
          });
        }
      });
    });
  }

  protected readonly handlers = {
    ...super.baseHandlers,
    TREND_ADD_SERIES: this.addSeries,
    TREND_ADD_SERIES_FROM_CAPSULE: this.addSeriesFromCapsule,
    TREND_UNSELECT_ALL_CAPSULES: this.unselectAllCapsuleSeries,
    TREND_REMOVE_SERIES_FROM_CAPSULE: this.removeSeriesFromCapsule,
    TREND_SERIES_RESULTS_SUCCESS: this.addData,
    TREND_MULTIPLE_SERIES_DATA_RESULTS_SUCCESS: this.addSignalSegmentsData,
    TREND_SERIES_CLEAR_DATA: this.clearData,
    TREND_SET_EDITING_SERIES_ID: this.setEditingId,
    TREND_SET_CHART_SERIES_PREVIEW: this.setPreviewChartItem,
    TREND_REMOVE_CHART_SERIES_PREVIEW: this.removePreviewChartItem,
    TREND_REMOVE_ALL_CAPSULE_SERIES: this.removeAllCapsuleSeries,
  };

  /**
   * Adds a series item.
   *
   * @param {Object} payload - Object container for arguments
   * @param {String} payload.id - ID of the new item
   * @param {String} payload.name - Name of the new item
   * @param {String} payload.lane - Lane of the new item
   * @param {String} payload.alignment - Alignment of the new item
   * @param {String} [payload.color] - Color hex code (e.g. #CCCCCC)
   */
  private addSeries(payload: AddSeriesPayload): void {
    const interpolationMethodValue =
      typeof payload.interpolationMethod === 'string'
        ? payload.interpolationMethod
        : (payload.interpolationMethod?.value ?? undefined);

    const seriesProps: SeriesProps = {};
    if (payload.color) {
      seriesProps.color = payload.color;
    }
    if (interpolationMethodValue) {
      seriesProps.interpolationMethod = interpolationMethodValue;
    }
    if (payload.laneReservation) {
      seriesProps.laneReservation = payload.laneReservation;
    }

    const series = this.createSeries(payload.id, payload.name, payload.lane, payload.alignment, seriesProps);
    this.state.push('items', series);
  }

  /**
   * Private helper function to add a series to the store using the specified properties.
   *
   * @param {String} id - ID to use for the new series
   * @param {String} name - Name to use for the new series
   * @param {String} lane - Lane to use for the new series
   * @param {String} alignment - Alignment to use for the new series
   * @param {SeriesProps} props - Object containing properties to apply to the new series
   *
   * @returns {Object} Newly created series object.
   */
  private createSeries(id: string, name: string, lane: number, alignment: string, props: SeriesProps): SeriesStoreItem {
    const defaults: SeriesProps = {
      data: [],
      samples: [],
      isStringSeries: false,
      yAxisType: Y_AXIS_TYPES.LINEAR,
      dashStyle: props.dashStyle ?? DASH_STYLES.SOLID,
      lane,
      axisAlign: alignment,
      axisVisibility: props.axisVisibility ?? true,
      axisAutoScale: props.axisAutoScale ?? true,
      yAxisConfig: props.yAxisConfig ?? {},
      lineWidth: props.lineWidth ?? 1,
      sampleDisplayOption: props.sampleDisplayOption ?? SAMPLE_OPTIONS.LINE,
      interpolationMethod:
        typeof props.interpolationMethod === 'string' ? props.interpolationMethod : (props.interpolationMethod ?? ''),
    };

    return createItem(id, name, ITEM_TYPES.SERIES, { ...defaults, ...props }) as SeriesStoreItem;
  }

  /**
   * Creates a series item from a capsule item, and adds the new series item to store. The created
   * series item is created with a new guid and the name property as the capsule name concatenated
   * with the interest (i.e. series) name. Properties from the capsule and other properties copied
   * from the capsule and interest.
   *
   * @param {Object} payload - Object container for arguments
   * @param {Object} payload.capsule - Capsule from which to create a series.
   * @param {String} payload.capsule.id - ID of the capsule, to use as the ID of the created series.
   * @param {String} payload.capsule.name - Name of the capsule, to use as the name of the created series.
   * @param {String} payload.capsule.similarity - Profile search capsule property to copy to
   *   the created series.
   * @param {Object} payload.interest - Interest item from the capsule to use as a source for some properties.
   * @param {String} payload.interest.id - ID of the interest.
   */
  private addSeriesFromCapsule(payload: AddSeriesFromCapsulePayload): void {
    const separateByValue =
      payload.capsule.properties?.[sqTrendStore.separateByProperty as keyof CapsuleItem['properties']];
    const colorByValue = payload.capsule.properties?.[sqTrendStore.colorByProperty as keyof CapsuleItem['properties']];

    const resolvedSeparateBy =
      typeof separateByValue === 'string'
        ? separateByValue
        : separateByValue != null
          ? String(separateByValue)
          : undefined;
    const resolvedColorBy =
      typeof colorByValue === 'string' ? colorByValue : colorByValue != null ? String(colorByValue) : undefined;

    const series = this.createSeries(
      createUniqueCapsuleSeriesId(payload.capsule.id, payload.interest.id),
      payload.interest.name ?? '',
      payload.interest.lane ?? 0,
      payload.interest.axisAlign ?? 'left',
      {
        startTime: payload.capsule.startTime ?? undefined,
        // Without this, series with no endTime were displayed “invisibly” on the chart. Hover would show points,
        // but the trend line would not appear. Though we had tried to avoid using this big hammer, in the end it
        // was necessary. CRAB-18916
        endTime: payload.capsule.endTime ?? this.getAlternateEndTime(),
        duration: payload.capsule.duration,
        capsuleId: payload.capsule.id,
        interestId: payload.interest.id,
        isChildOf: payload.interest.id,
        column: getDefaultCompareViewPropertyValue(resolvedSeparateBy),
        colorBy: getDefaultCompareViewPropertyValue(resolvedColorBy),
        otherChildrenOf: [payload.capsule.isChildOf, payload.capsule.id].filter(Boolean),
        childType: ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE,
        assets: payload.interest.assets,
        selected: Boolean(payload.capsule.selected || payload.interest.selected),
        capsuleSegmentData: [],
        color: payload.interest.color,
        description: payload.interest.description,
        formatOptions: payload.interest.formatOptions,
        sampleDisplayOption: payload.interest.sampleDisplayOption,
        dashStyle: payload.interest.dashStyle,
        axisVisibility: payload.interest.axisVisibility,
        axisAutoScale: payload.interest.axisAutoScale,
        lineWidth: payload.interest.lineWidth,
        rightAxis: payload.interest.rightAxis,
        showDataLabels: payload.interest.showDataLabels,
      },
    );

    this.state.push('items', series);
  }

  /**
   * Handles unselecting all capsuleSeries. This is done because selection status of capsuleSeries is kept in sync
   * with capsules.
   */
  private unselectAllCapsuleSeries() {
    _.forEach(this.getCapsuleSeries(), (item) => this.setProperty(item.id, 'selected', false));
  }

  /**
   * Removes any series that were added from a capsule item and interest series.
   *
   * @param {Object} payload - Object container for arguments
   * @param payload.seriesFromCapsules - an array of series from capsules to be removed
   */
  private removeSeriesFromCapsule(payload: { seriesFromCapsules: { capsuleId: string; interestId: string }[] }) {
    const items = _.filter(this.getCapsuleSeries(), (seriesFromCapsule) =>
      _.some(payload.seriesFromCapsules, {
        capsuleId: seriesFromCapsule.capsuleId,
        interestId: seriesFromCapsule.interestId,
      }),
    );
    this.removeItems({ items });
  }

  /**
   * Adds series data points to a series item.
   *
   * @param {Object} payload - Object container for arguments
   * @param {String} payload.id - ID of the item on which to add samples
   * @param {Object[]} payload.samples - Array of data points
   * @param {Number} payload.samples[].key - x-value of a data point
   * @param {Number} payload.samples[].value - y-value of a data point
   * @param {Boolean} payload.samples[].isUncertain - true if this data point is uncertain
   * @param {String} payload.valueUnitOfMeasure - Unit of Measure to be displayed
   * @param {Number} payload.warningCount - the count of warnings that will be passed to the base item store
   * @param {Object[]} payload.warningLogs - the log of warnings that will be passed to the base item store
   */
  private addData(payload: AddDataPayload): void {
    const cursor = this.getItemCursor(payload.id);
    if (!cursor.exists()) {
      return;
    }

    const shadedAreaLower = cursor.get('shadedAreaLower') as number | null | undefined;
    const shadedAreaUpper = cursor.get('shadedAreaUpper') as number | null | undefined;
    const hasBounds = shadedAreaLower != null && shadedAreaUpper != null;

    const minTimeValue = getMinTime(cursor);
    const minTime = minTimeValue == null ? undefined : minTimeValue;
    const color = cursor.get('color');
    const sampleDisplayOption = (cursor.get('sampleDisplayOption') as string) ?? SAMPLE_OPTIONS.LINE;
    const dataProps = getDataProps({
      context: getSeriesDataContext(),
      payload: {
        stringEnum: payload.stringEnum,
        samples: payload.samples as DataSample[],
        valueUnitOfMeasure: payload.valueUnitOfMeasure ?? '',
        timingInformation: payload.timingInformation,
        meterInformation: payload.meterInformation,
        interpolationMethod: payload.interpolationMethod,
        capsuleSegmentSamples:
          payload.capsuleSegmentSamples ?? (cursor.get('capsuleSegmentSamples') as SeriesDataSample[]),
        color,
        lineWidth: cursor.get('lineWidth') as number | undefined,
      },
      sampleDisplayOption,
      minTime,
      hasBounds,
    });

    cursor.merge(dataProps);

    // Ensure that the trend is displayed within its lane when it first appears on the chart; the
    // computed extremes go to the runtime store, the rest of the config is merged onto the item
    if (this.addPreview(this.getNonCapsuleSeries()).some((series) => series.id === payload.id)) {
      cursor.merge(updateComputedYAxisExtremes(payload.id));
    }
  }

  /**
   * Analyzes the payload, extracts the data for each capsule series and updates the store. The complete data for
   * each capsule is taken from the payload, even if the capsules overlaps. The formula used to retrieve the data
   * is created using {@code buildSignalSegmentsFormula}.
   * @param {Capsule[]} requestCapsules - Information about the capsules used to request data.
   * @param {Object[]} samples - Array of data points
   */
  private addSignalSegmentsData({
    id,
    requestCapsules,
    samples: allSamples,
    valueUnitOfMeasure,
    timingInformation,
    meterInformation,
    interpolationMethod,
    stringEnum,
  }: SignalSegmentsPayload): void {
    // Apply data for segments to main signal
    this.addData({
      id,
      valueUnitOfMeasure,
      timingInformation,
      meterInformation,
      interpolationMethod,
      samples: allSamples,
      stringEnum: stringEnum ?? createStringEnum(allSamples),
    });

    const capsuleSeries = this.getCapsuleSeries();
    const resolvedStringEnum = stringEnum ?? createStringEnum(allSamples);

    requestCapsules.forEach(({ interestId, capsuleId, startTime, endTime }) => {
      const capsule = capsuleSeries.find(
        (series) => series.interestId === interestId && series.capsuleId === capsuleId,
      );

      if (!capsule) {
        return;
      }

      const startIndexCandidate = _.sortedIndexBy<SeriesDataSample>(
        allSamples,
        { key: startTime * NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND },
        'key',
      );
      const startIndex =
        startIndexCandidate < allSamples.length && 'value' in (allSamples[startIndexCandidate] ?? {})
          ? startIndexCandidate
          : startIndexCandidate + 1;

      const endIndex = _.sortedIndexBy<SeriesDataSample>(
        allSamples,
        { key: endTime * NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND },
        'key',
      );

      const samples = allSamples.slice(startIndex, endIndex + 1);
      const capsulePayload: AddDataPayload = {
        id: capsule.id,
        samples,
        valueUnitOfMeasure,
        timingInformation,
        meterInformation,
        interpolationMethod,
        stringEnum: resolvedStringEnum,
      };

      // addData without updating the capsule alignment. We do it once at the end
      this.addData(capsulePayload);
    });
  }

  /**
   * Clears series data.
   *
   * @param {Object} payload - Object container for arguments
   * @param {String} payload.id - ID of the item to clear
   */
  private clearData(payload: { id: string }): void {
    const cursor = this.getItemCursor(payload.id);
    if (cursor.exists()) {
      cursor.set('data', []);
    }
  }

  /**
   * Sets the id of the Series being edited.
   *
   * @param {Object} payload - Object container for arguments
   * @param {String} payload.id - the id.
   */
  private setEditingId(payload: { id: string | undefined }): void {
    this.state.set('editingId', payload.id);
    this.state.set('previewSeriesDefinition', null);
  }

  /**
   * Sets the preview series so they can be displayed. Also stores an Object that defines how to generate those
   * preview series so they can be updated when the users scrolls.
   *
   * @param {Object} payload - Object container for arguments
   * @param {String} payload.formula - formula used to create the series
   * @param {Object[]} payload.parameters - Object array describing the parameters used to create the series
   * @param {String} payload.id - ID of the existing item
   * @param {String} payload.name - Name to use for the new series
   * @param {Object[]} payload.data - Array of data points
   * @param {Number} payload.samples[].key - x-value of a data point
   * @param {Number} payload.samples[].value - y-value of a data point
   * @param {Boolean} payload.samples[].isUncertain - true if this data point is uncertain
   * @param {String} payload.valueUnitOfMeasure - Unit of Measure to be displayed
   * @param {String} payload.lane - Lane of the new item
   * @param {String} payload.alignment - Alignment of the new item
   * @param {String} payload.color - Color hex code (e.g. #CCCCCC)
   */
  private setPreviewChartItem(payload: PreviewSeriesPayload): void {
    const rawId = payload.id ?? PREVIEW_ID;
    const previewPayloadId = rawId.startsWith(PREVIEW_PREFIX) ? rawId : `${PREVIEW_PREFIX}${rawId}`;
    const previewId = rawId !== PREVIEW_ID ? previewPayloadId : PREVIEW_ID;
    const sampleDisplayOption = payload.sampleDisplayOption ?? SAMPLE_OPTIONS.LINE;

    if (!payload.samples || payload.samples.length === 0) {
      this.state.set('previewSeriesDefinition', undefined);
      this.state.set('previewChartItem', undefined);
      return;
    }

    const previewDefinition: PreviewSeriesDefinition = {
      formula: payload.formula,
      parameters: payload.parameters,
      id: previewId,
      color: payload.color ?? undefined,
      sampleDisplayOption,
    };
    this.state.set('previewSeriesDefinition', previewDefinition);

    const dataProps = getDataProps({
      context: getSeriesDataContext(),
      payload: {
        stringEnum: payload.stringEnum,
        samples: payload.samples as DataSample[],
        valueUnitOfMeasure: payload.valueUnitOfMeasure ?? '',
        timingInformation: payload.timingInformation,
        meterInformation: payload.meterInformation,
        interpolationMethod: payload.interpolationMethod,
        capsuleSegmentSamples: payload.capsuleSegmentSamples ?? [],
        color: payload.color ?? undefined,
        lineWidth: payload.lineWidth,
      },
      sampleDisplayOption,
    });

    const previewItem = this.createSeries(previewId, payload.name, payload.lane, payload.alignment, {
      ...dataProps,
      color: payload.color ?? undefined,
      lineWidth: payload.lineWidth,
    });

    this.state.set('previewChartItem', previewItem);
    this.state.merge('previewChartItem', getYAxisConfig(previewId) as Record<string, unknown>);
  }

  /**
   * Removes the previewChartItem.
   */
  private removePreviewChartItem() {
    const previewChartItem = this.state.get('previewChartItem') as SeriesStoreItem | undefined;
    if (previewChartItem) {
      if (typeof previewChartItem.color === 'string') {
        removeColor(previewChartItem.color);
      }
      this.state.merge({
        previewChartItem: undefined,
        previewSeriesDefinition: undefined,
      });
    }
  }

  /**
   * Handles removing all capsuleSeries.
   */
  private removeAllCapsuleSeries() {
    this.removeItems({ items: this.getCapsuleSeries() });
  }

  /**
   * Returns all series that are converted from a capsule
   */
  private getCapsuleSeries(): SeriesStoreItem[] {
    const hideUnselectedItems = Boolean(sqTrendStore.hideUnselectedItems);
    const referenceInterestIds = getSeriesIdsByCalculationType(TREND_TOOLS.REFERENCE, this) ?? [];

    let capsuleStoreItems: TrendCapsuleStoreItem[] = [...(sqTrendCapsuleStore.items as TrendCapsuleStoreItem[])];
    const someCapsulesSelected = capsuleStoreItems.some((capsule) => capsule.selected);
    const longestCapsuleFromStore = _.maxBy(capsuleStoreItems, (capsule) => capsule.duration ?? 0);

    if (someCapsulesSelected) {
      capsuleStoreItems = capsuleStoreItems.filter((capsule) => capsule.selected);

      // When we aren't dimming, we want to make sure to keep the longest overall capsule so that when we filter out
      // the reference profiles, we show the longest slightly transparent and the selected one with full opacity
      if (!hideUnselectedItems && longestCapsuleFromStore) {
        capsuleStoreItems.push(longestCapsuleFromStore);
      }
    }

    const capsuleIds = capsuleStoreItems.map((capsule) => capsule.id);

    const [referenceCapsuleSeries, nonReferenceCapsuleSeries] = _.chain(this.seriesItems)
      .filter((item) => item.isChildOf && item.childType === ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE)
      .uniqBy('id')
      .partition((item) => referenceInterestIds.includes(item.isChildOf as string))
      .value() as [SeriesStoreItem[], SeriesStoreItem[]];

    const filteredAndGroupedReferenceCapsuleSeries = _.chain(referenceCapsuleSeries)
      .filter((item) => item.capsuleId && capsuleIds.includes(item.capsuleId))
      .groupBy((item) => item.idChildOf ?? '')
      .map((group) => _.groupBy(group, 'id'))
      .value() as Array<Record<string, SeriesStoreItem[]>>;

    const referenceCapsuleSeriesToKeep: SeriesStoreItem[] = [];

    filteredAndGroupedReferenceCapsuleSeries.forEach((referenceGroup) => {
      Object.values(referenceGroup).forEach((capsuleGroup) => {
        if (
          someCapsulesSelected &&
          !hideUnselectedItems &&
          longestCapsuleFromStore &&
          !longestCapsuleFromStore.selected
        ) {
          const longestOverallIndex = capsuleGroup.findIndex(
            (series) => series.capsuleId === longestCapsuleFromStore.id,
          );
          if (longestOverallIndex >= 0) {
            const [longestOverall] = capsuleGroup.splice(longestOverallIndex, 1);
            if (longestOverall) {
              referenceCapsuleSeriesToKeep.push(longestOverall);
            }
          }
        }

        const maxSeries = _.maxBy(
          capsuleGroup,
          (series) => series.duration ?? (series.endTime ?? 0) - (series.startTime ?? 0),
        );
        if (maxSeries) {
          referenceCapsuleSeriesToKeep.push(maxSeries);
        }
      });
    });

    return [...nonReferenceCapsuleSeries, ...referenceCapsuleSeriesToKeep];
  }

  /**
   * Returns all series that are not converted from capsules
   */
  private getNonCapsuleSeries() {
    return this.seriesItems.filter(
      (item) => !item.isChildOf || item.childType !== ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE,
    );
  }

  private getAlternateEndTime() {
    const displayRangeEnd = sqDurationStore.displayRange.end.valueOf();
    const now = Date.now();

    return now < displayRangeEnd ? now : displayRangeEnd;
  }

  /**
   * This function manages the correct inclusion of preview series. If a new Search is performed then the
   * resulting preview series are simply appended. If an existing search is edited then the original series are
   * omitted and replaced with the preview series.
   */
  private addPreview(items: SeriesStoreItem[]): SeriesStoreItem[] {
    const series = [...items];
    const editingId = this.state.get('editingId') as string | null | undefined;
    const previewSeriesDef = this.state.get('previewSeriesDefinition') as PreviewSeriesDefinition | null | undefined;
    const previewChartItem = this.state.get('previewChartItem') as SeriesStoreItem | SeriesStoreItem[] | undefined;
    const previewItems = Array.isArray(previewChartItem)
      ? previewChartItem.filter(Boolean)
      : previewChartItem
        ? [previewChartItem]
        : [];
    const displayPreview = !_.isEmpty(previewSeriesDef) && previewItems.length > 0;

    if (editingId != null && displayPreview) {
      const previewReplacement = previewItems[0];
      const itemIndex = series.findIndex((item) => item.id === editingId);
      if (itemIndex >= 0 && previewReplacement) {
        series.splice(itemIndex, 1, previewReplacement);
      } else {
        series.push(...previewItems);
      }
    } else if (displayPreview) {
      series.push(...previewItems);
    }

    return series;
  }

  /**
   * Overrides the behavior of TREND_SET_COLOR to take zones and series from capsule into account when changing color
   *
   * Called when the color for an item changes, should set the color on the item as necessary
   *
   * @param {Object} item - serialized item object
   * @param {Object} color - color that should be set
   * @param {string} [parentId] - what was the id of the parent that triggered the change
   */
  protected onSetColor(item: Record<string, unknown> | undefined, color: string, parentId?: string): void {
    const series = item as SeriesStoreItem | undefined;
    if (parentId && series?.childType === ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE && parentId !== series.interestId) {
      return; // Colors for signal from capsule come from the signal, not the condition
    }

    if (!series) {
      return;
    }

    const cursor = this.getItemCursor(series.id);
    cursor.set('color', color);

    (series.zones ?? []).forEach((zone, index) => {
      if (_.has(zone, 'color')) {
        cursor.set(['zones', index, 'color'], color);
      }
    });
  }

  /**
   * Overrides the behavior of TREND_SET_SELECTED so that selection from capsules determine the selection of
   * series from capsules
   *
   * Called when the selection for an item change, should set the color on the item as necessary
   *
   * @param {Object} item - serialized item object
   * @param {Object} selected - selection status
   * @param {string} [parentId] - what was the id of the parent that triggered the change
   */
  protected onSetSelected(item: Record<string, unknown> | undefined, selected: boolean, parentId?: string): void {
    const series = item as SeriesStoreItem | undefined;
    if (
      parentId &&
      series?.childType === ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE &&
      parentId !== series.capsuleId &&
      parentId !== series.interestId
    ) {
      return; // Selection status for series from capsule should only come from its capsule or interest signal.
    }

    if (!series) {
      return;
    }

    this.state.select('items', this.findItemIndex(series.id)).set('selected', selected);
    if (this.isPreviewItem(series.id)) {
      this.state.select('previewChartItem').set('selected', selected);
    }
  }

  /**
   * This changes the behavior of TREND_SET_CUSTOMIZATIONS to handle changes to the sample display
   *
   * This function ensures that the data property is updated as needed (if signals are displayed
   * as bar charts their data is "prepared" differently than if the signal is visualized as a
   * line - different "compression" and optimizations are applied).
   *
   * Called when the customizations for an item change, should set the customizations on the item as necessary
   *
   * @param {Object} item - serialized item object
   * @param {Object} customizations - customize properties being set
   * @param {String} customizations.sampleDisplayOption - one of SAMPLE_OPTIONS
   * @param {string} [parentId] - what was the id of the parent that triggered the change
   */
  protected onSetCustomizations(
    item: Record<string, unknown> | undefined,
    customizations: Record<string, unknown>,
    parentId?: string,
  ): void {
    const series = item as SeriesStoreItem | undefined;
    if (!series) {
      return;
    }

    const cursor = this.getItemCursor(series.id);
    cursor.merge(customizations);

    const shouldRebuildData =
      ('sampleDisplayOption' in customizations && customizations.sampleDisplayOption) || 'lineWidth' in customizations;

    if (shouldRebuildData) {
      const effectiveSampleDisplayOption = (cursor.get('sampleDisplayOption') as string) ?? SAMPLE_OPTIONS.LINE;
      const effectiveLineWidth = (cursor.get('lineWidth') as number | undefined) ?? series.lineWidth;
      const hasBounds = series.shadedAreaLower != null && series.shadedAreaUpper != null;
      const minTime = getMinTime(cursor) ?? 0;
      cursor.merge(
        transformDataPoints({
          ...getSeriesDataContext(),
          rawSamples: series.samples ?? [],
          color: series.color ?? '#000000',
          lineWidth: effectiveLineWidth,
          minTime,
          sampleDisplayOption: effectiveSampleDisplayOption,
          hasBounds,
          stringEnum: series.stringEnum as StringEnum[] | undefined,
        }),
      );
    }
  }

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @returns {Object} The dehydrated items.
   */
  dehydrate(): TrendSeriesDehydratedState {
    const extraPropsToDehydrate = ['displayedAncillaryItemIds', 'interpolationMethod'];

    return {
      items: _.chain(this.seriesItems)
        .filter(shouldDehydrateItem)
        .map((item) => pruneDehydratedItemProps(item, extraPropsToDehydrate))
        .value(),
      editingId: this.state.get('editingId') as string | null | undefined,
      previewSeriesDefinition: this.state.get('previewSeriesDefinition') as PreviewSeriesDefinition | null | undefined,
    };
  }

  /**
   * Re-creates the series. Calls the action to rehydrate the data points.
   *
   * @param {Object} dehydratedState Previous state usually obtained from `dehydrate` method.
   *
   * @return {Object} A promise that is fulfilled when items are completely rehydrated.
   */
  rehydrate(dehydratedState: TrendSeriesDehydratedState): void {
    this.state.set(
      'items',
      dehydratedState.items.map((item) =>
        this.createSeries(
          item.id,
          item.name ?? '',
          item.lane ?? 0,
          (item.axisAlign as string | undefined) ?? (item.alignment as string | undefined) ?? 'left',
          _.omit(item, ['id', 'name', 'lane', 'alignment']) as SeriesProps,
        ),
      ),
    );

    this.state.set('editingId', dehydratedState.editingId);
    this.state.set('previewSeriesDefinition', dehydratedState.previewSeriesDefinition);
  }
}
